import { CountryCodeMapper } from "./addressLine";
import { QuoteCopy } from "./i18n/QuoteCopy";
import type { CopyKey } from "./i18n/copyTypes";
import type { FormState } from "./types";

// Keep in sync with server/app/location_format.py

const CITY_PATTERN = /^[\p{L}'’][\p{L}'’.\- ]{0,78}$/u;

const POSTAL_PATTERNS: Record<string, RegExp> = {
  AT: /^\d{4}$/,
  BE: /^\d{4}$/,
  BG: /^\d{4}$/,
  CH: /^\d{4}$/,
  CY: /^\d{4}$/,
  CZ: /^\d{3}\s?\d{2}$/,
  DE: /^\d{5}$/,
  DK: /^\d{4}$/,
  EE: /^\d{5}$/,
  ES: /^\d{5}$/,
  FI: /^\d{5}$/,
  FR: /^\d{5}$/,
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/,
  GR: /^\d{3}\s?\d{2}$/,
  HR: /^\d{5}$/,
  HU: /^\d{4}$/,
  IE: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/,
  IT: /^\d{5}$/,
  LT: /^(?:LT-?)?\d{5}$/,
  LU: /^\d{4}$/,
  LV: /^(?:LV-?)?\d{4}$/,
  MT: /^[A-Z]{3}\s?\d{4}$/,
  NL: /^\d{4}\s?[A-Z]{2}$/,
  NO: /^\d{4}$/,
  PL: /^\d{2}-?\d{3}$/,
  PT: /^\d{4}-?\d{3}$/,
  RO: /^\d{6}$/,
  SE: /^\d{3}\s?\d{2}$/,
  SI: /^\d{4}$/,
  SK: /^\d{3}\s?\d{2}$/,
};

const GENERIC_POSTAL =
  /^(?:\d{4}\s?[A-Z]{2}|\d{2}-?\d{3}|\d{4}-?\d{3}|\d{3}\s?\d{2}|\d{3,6}|[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}|[A-Z]{3}\s?\d{4}|[A-Z]\d{2}\s?[A-Z0-9]{4})$/;

export class RouteCityFormat {
  valid(value: string): boolean {
    const city = value.trim();
    if (!city) return true;
    if (/\d/.test(city)) return false;
    const letters = city.match(/\p{L}/gu) ?? [];
    return letters.length >= 2 && CITY_PATTERN.test(city);
  }
}

export class RoutePostalFormat {
  valid(value: string, countryCode: string): boolean {
    const postal = value.trim();
    if (!postal) return true;
    const compact = postal.toUpperCase().replace(/\s+/g, " ").trim();
    const pattern = POSTAL_PATTERNS[countryCode] ?? GENERIC_POSTAL;
    return pattern.test(compact);
  }
}

interface RouteFormatSide {
  postal: string;
  city: string;
  country: string;
  postalId: "loadPostal" | "unloadPostal";
  cityId: "loadCity" | "unloadCity";
  postalMessage: CopyKey;
  cityMessage: CopyKey;
}

export class RouteLocationFormatValidator {
  constructor(
    private readonly cityFormat = new RouteCityFormat(),
    private readonly postalFormat = new RoutePostalFormat(),
    private readonly countries = new CountryCodeMapper(),
  ) {}

  validate(form: FormState, copy: QuoteCopy = QuoteCopy.english()): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const side of this.sides(form)) {
      this.assign(errors, side.postalId, side.postal, copy.t(side.postalMessage), (value) =>
        this.postalFormat.valid(value, this.countries.toCode(side.country)),
      );
      this.assign(errors, side.cityId, side.city, copy.t(side.cityMessage), (value) =>
        this.cityFormat.valid(value),
      );
    }
    return errors;
  }

  private sides(form: FormState): RouteFormatSide[] {
    return [
      {
        postal: form.loadPostal,
        city: form.loadCity,
        country: form.loadCountry,
        postalId: "loadPostal",
        cityId: "loadCity",
        postalMessage: "error.loadPostalInvalid",
        cityMessage: "error.loadCityInvalid",
      },
      {
        postal: form.unloadPostal,
        city: form.unloadCity,
        country: form.unloadCountry,
        postalId: "unloadPostal",
        cityId: "unloadCity",
        postalMessage: "error.unloadPostalInvalid",
        cityMessage: "error.unloadCityInvalid",
      },
    ];
  }

  private assign(
    errors: Record<string, string>,
    id: string,
    value: string,
    message: string,
    valid: (value: string) => boolean,
  ): void {
    const text = value.trim();
    if (!text) return;
    if (!valid(text)) errors[id] = message;
  }
}
